/**
 * THE CONTACT LIST — everyone a service advisor might need to reach.
 *
 * Anthony: "make sure service advisors have a contact list with everyone
 * contact they can access under their more option."
 *
 * A shop's phone book is not one list. In a morning an advisor rings a
 * customer about a price, a technician about where they are, and a parts
 * counter about a backorder — and until now those lived on three different
 * screens, none of which was a phone book. This is the phone book.
 *
 * IT SHOWS WHAT THE VIEWER IS ALLOWED TO SEE, and nothing more. That is
 * not a rule this file enforces: the database decides, so a technician
 * opening it sees the customers on their own jobs and not the shop's whole
 * book. The same page is safe for everybody because the answer comes from
 * row level security rather than from a check written here — which is the
 * only kind of answer worth trusting.
 *
 * TAP TO CALL, TAP TO TEXT, TAP TO NAVIGATE. Every row is a thing you do,
 * not a thing you read. Nobody opens a contact list to admire it.
 */

import { db } from "../data/client.ts";
import { esc, mount } from "../ui/dom.ts";
import { findBarHtml, wireFindBar } from "../ui/findbar.ts";
import type { Role } from "../domain/permissions.ts";

type Kind = "customer" | "team" | "supplier";

type Contact = {
  kind: Kind;
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  detail: string;
  href: string | null;
};

const TABS: Array<{ key: Kind | "all"; label: string }> = [
  { key: "all", label: "Everyone" },
  { key: "customer", label: "Customers" },
  { key: "team", label: "The shop" },
  { key: "supplier", label: "Suppliers" },
];

let tab: Kind | "all" = "all";

const tel = (p: string | null) => (p ?? "").replace(/[^\d+]/g, "");

function row(c: Contact): string {
  return `
    <div class="contact">
      <div class="contact-main">
        <div class="contact-name">
          ${c.href ? `<a href="${esc(c.href)}">${esc(c.name)}</a>` : esc(c.name)}
          <span class="pill c-${c.kind}">${c.kind === "team" ? "shop" : c.kind}</span>
        </div>
        <div class="contact-sub">${esc(c.detail)}</div>
      </div>
      <div class="contact-acts">
        ${c.phone ? `<a class="navlink" href="tel:${esc(tel(c.phone))}">call</a>` : ""}
        ${c.phone ? `<a class="navlink" href="sms:${esc(tel(c.phone))}">text</a>` : ""}
        ${c.email ? `<a class="navlink" href="mailto:${esc(c.email)}">email</a>` : ""}
      </div>
    </div>`;
}

export async function contactsPage(_role: Role = "viewer"): Promise<void> {
  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Contacts</h1>
      <p>Everyone you can reach, in one place. You only see what your login
         already lets you see.</p></header>
    <div class="tabs wide" id="ctabs">
      ${TABS.map((t) => `<button class="tab ${t.key === tab ? "on" : ""}"
        data-k="${t.key}">${esc(t.label)}</button>`).join("")}
    </div>
    <div id="findhost">${findBarHtml("Find anyone — name, number, email")}</div>
    <div class="contacts" id="clist"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  document.querySelectorAll<HTMLButtonElement>("#ctabs .tab").forEach((b) =>
    b.addEventListener("click", () => { tab = b.dataset["k"] as Kind | "all"; void contactsPage(_role); }));

  const host = document.getElementById("clist");
  if (!host) return;

  try {
    const sb = await db();

    // Three reads, in parallel, each of them already fenced by the
    // database to what this login may see.
    const [people, techs, custs, vendors] = await Promise.all([
      sb.from("profiles").select("id,full_name,email,phone,role,active")
        .eq("active", true).order("full_name"),
      // Technicians WITHOUT a login. Most of the shop's wrenches have no
      // account, and a phone book that only lists people who can sign in
      // is missing exactly the people an advisor rings from the counter.
      sb.from("technicians").select("id,name,phone,profile_id,active")
        .eq("active", true).order("name"),
      // Customers are the long list, so it is capped and the search is
      // what finds somebody further down. A phone book you scroll for
      // three thousand rows is not a phone book.
      sb.from("customers").select("id,full_name,phone,email,address")
        .order("updated_at", { ascending: false, nullsFirst: false }).limit(400),
      sb.from("vendors").select("id,name,phone,url,account_ref,active")
        .eq("active", true).order("rank"),
    ]);

    const out: Contact[] = [];

    for (const p of (people.data ?? []) as Array<{
      id: string; full_name: string; email: string | null;
      phone: string | null; role: string;
    }>) {
      out.push({
        kind: "team", id: p.id, name: p.full_name,
        phone: p.phone, email: p.email,
        detail: p.role, href: null,
      });
    }

    for (const t of (techs.data ?? []) as Array<{
      id: string; name: string; phone: string | null; profile_id: string | null;
    }>) {
      if (t.profile_id) continue;              // already listed above
      out.push({
        kind: "team", id: t.id, name: t.name,
        phone: t.phone, email: null,
        detail: "technician", href: null,
      });
    }

    for (const c of (custs.data ?? []) as Array<{
      id: string; full_name: string; phone: string | null;
      email: string | null; address: string | null;
    }>) {
      out.push({
        kind: "customer", id: c.id, name: c.full_name,
        phone: c.phone, email: c.email,
        detail: c.address ?? c.email ?? "no address on file",
        href: `#/customer/${c.id}`,
      });
    }

    for (const v of (vendors.data ?? []) as Array<{
      id: string; name: string; phone: string | null;
      url: string | null; account_ref: string | null;
    }>) {
      out.push({
        kind: "supplier", id: v.id, name: v.name,
        phone: v.phone, email: null,
        detail: v.account_ref ? `account ${v.account_ref}` : (v.url ?? "supplier"),
        href: null,
      });
    }

    const shown = tab === "all" ? out : out.filter((c) => c.kind === tab);

    host.innerHTML = shown.length
      ? shown.map(row).join("")
      : `<div class="empty">Nobody here.</div>`;

    wireFindBar({ hostId: "findhost", listId: "clist", noun: "contact" });
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}
